import { Suspense, lazy } from 'react'
import type { PartialRouteObject } from 'react-router'
import { Navigate } from 'react-router-dom'
import AuthGuard from './components/AuthGuard'
import DashboardLayout from './components/dashboard/DashboardLayout'
import GuestGuard from './components/GuestGuard'
import LoadingScreen from './components/LoadingScreen'

const Loadable = (Component) => (props) =>
  (
    <Suspense fallback={<LoadingScreen />}>
      <Component {...props} />
    </Suspense>
  )

// Authentication pages

const Login = Loadable(lazy(() => import('./pages/authentication/Login')))

// Clio pages
const AddToClio = Loadable(lazy(() => import('./pages/AddToClio')))

// Dashboard pages

const Account = Loadable(lazy(() => import('./pages/dashboard/MyAccount')))
const Analytics = Loadable(lazy(() => import('./pages/dashboard/Analytics')))
const Overview = Loadable(lazy(() => import('./pages/dashboard/Overview')))
const Contacts = Loadable(lazy(() => import('./pages/dashboard/ContactsList')))
const Sms = Loadable(lazy(() => import('./pages/dashboard/Sms')))
const Calls = Loadable(lazy(() => import('./pages/dashboard/Calls')))
const Communications = Loadable(
  lazy(() => import('./pages/dashboard/Communications'))
)
const Voicemails = Loadable(lazy(() => import('./pages/dashboard/Voicemail')))

// Error pages

const AuthorizationRequired = Loadable(
  lazy(() => import('./pages/AuthorizationRequired'))
)
const NotFound = Loadable(lazy(() => import('./pages/NotFound')))
const ServerError = Loadable(lazy(() => import('./pages/ServerError')))

const routes: PartialRouteObject[] = [
  {
    path: 'authentication',
    children: [
      {
        path: 'login',
        element: (
          <GuestGuard>
            <Login />
          </GuestGuard>
        )
      },
      {
        path: 'login-unguarded',
        element: <Login />
      }
    ]
  },
  {
    path: 'add-to-clio',
    element: (
      <GuestGuard>
        <AddToClio />
      </GuestGuard>
    )
  },
  {
    path: 'dashboard',
    element: (
      <AuthGuard>
        <DashboardLayout />
      </AuthGuard>
    ),
    children: [
      {
        path: '/',
        element: <Navigate to="/dashboard/overview" />
      },
      {
        path: '/overview',
        element: <Overview />
      },
      {
        path: 'settings',
        children: [
          {
            path: '/',
            element: <Navigate to="/dashboard/settings/general" />
          },
          {
            path: ':currentTab',
            element: <Account />
          }
        ]
      },
      {
        path: 'calls',
        element: <Calls />
      },
      {
        path: 'communications',
        element: <Communications />
      },
      {
        path: 'voicemail',
        element: <Voicemails />
      },
      {
        path: 'sms',
        element: <Sms />
      },
      {
        path: 'contacts',
        element: <Contacts />
      }
    ]
  },
  {
    path: '*',
    children: [
      {
        path: '/',
        element: <Navigate to="/dashboard" />
      },
      {
        path: '401',
        element: <AuthorizationRequired />
      },
      {
        path: '404',
        element: <NotFound />
      },
      {
        path: '500',
        element: <ServerError />
      },
      {
        path: '*',
        element: <NotFound />
      }
    ]
  }
]

export default routes
